// src/store/useStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Points system configuration
const pointsSystem = {
  podcasts: {
    listenPerMinute: 0,
    completeBonus: 25,
    seriesComplete: 150,
    categoryMaster: 300,
    typeMultipliers: {
      'Deep Dive': 1.5,
      'Tutorial': 1.2,
      'Educational': 1.3,
      'Critique': 1.1,
      'Debate': 1.4,
      'Interview': 1.0
    }
  },
  courses: {
    lessonComplete: 25,
    moduleComplete: 100,
    courseComplete: 300,
    learningPathComplete: 1000,
    difficultyMultipliers: {
      'Beginner': 1.0,
      'Intermediate': 1.3,
      'Advanced': 1.7
    }
  },
  achievements: {
    common: 50,
    rare: 100,
    epic: 200,
    legendary: 500
  },
  daily: {
    firstActivity: 25,
    streakBonus: (streak) => Math.min(streak * 10, 100),
    weekendBonus: 50
  }
};

// Calculate podcast points - more challenging: need 85% completion, partial gives less
const calculatePodcastPoints = (podcast, listenDuration, isComplete = false) => {
  const system = pointsSystem.podcasts;
  let points = 0;
  
  if (isComplete) {
    const durationMinutes = parseInt(podcast.duration) || 30;
    const completionPercentage = (listenDuration / durationMinutes) * 100;
    
    if (completionPercentage >= 85) { // Increased from 90% to 85% but still challenging
      points += system.completeBonus;
      points *= system.typeMultipliers[podcast.episodeType] || 1.0;
    }
  } else {
    // Partial listening - reduced points (only 30% of completion bonus)
    const durationMinutes = parseInt(podcast.duration) || 30;
    const completionPercentage = (listenDuration / durationMinutes) * 100;
    if (completionPercentage >= 60) {
      points += system.completeBonus * 0.3;
      points *= system.typeMultipliers[podcast.episodeType] || 1.0;
    }
  }
  
  return Math.round(points);
};

const calculateCoursePoints = (course, activityType, difficulty = 'Beginner') => {
  const system = pointsSystem.courses;
  let points = 0;
  
  switch (activityType) {
    case 'lesson_complete':
      points = system.lessonComplete;
      break;
    case 'module_complete':
      points = system.moduleComplete;
      break;
    case 'course_complete':
      points = system.courseComplete;
      break;
    case 'learning_path_complete':
      points = system.learningPathComplete;
      break;
  }
  
  points *= system.difficultyMultipliers[difficulty] || 1.0;
  
  return Math.round(points);
};

// Helper functions
function isConsecutiveDay(lastDateStr, currentDateStr) {
  const lastDate = new Date(lastDateStr);
  const currentDate = new Date(currentDateStr);
  const diffTime = currentDate - lastDate;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays === 1;
}

function getPointsMessage(activityType, metadata = {}) {
  const messages = {
    'lesson_complete': 'Great progress on your lesson!',
    'module_complete': 'Module completed! Well done!',
    'course_complete': 'Course completed! Amazing work!',
    'podcast_listen': 'Keep up the great listening!',
    'podcast_complete': 'Podcast completed!',
    'badge_unlock': 'New badge unlocked!',
    'streak_bonus': `${metadata.streak} day streak!`,
    'first_activity': 'Daily streak maintained!'
  };
  return messages[activityType] || 'Great work!';
}

// Create the store
export const useStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      enrolledCourses: [],
      completedLessons: {},
      currentlyPlaying: null,
      accessTier: 'free',
      listeningHistory: [],

      // Points system state
      userPoints: 0,
      userLevel: 1,
      userRank: 'Novice',
      badges: [],
      dailyStreak: 0,
      lastActivityDate: null,

      // Learning time tracking
      learningTime: 0,
      dailyLearningTime: 0,
      lastLearningDate: new Date().toDateString(),
      courseLearningTime: {},
      podcastLearningTime: {},

      // Toast notifications
      toasts: [],

      // Profile picture state
      profilePicture: null,

      // Loading state for authentication
      loading: true,

      // ============ AUTHENTICATION ============
      loadUser: () => {
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const userData = JSON.parse(localStorage.getItem('user') || 'null');
            if (userData) {
              set({ 
                user: userData, 
                token: token,
                loading: false 
              });
            } else {
              set({ user: null, token: null, loading: false });
            }
          } else {
            set({ user: null, token: null, loading: false });
          }
        } catch (error) {
          console.error('Error loading user:', error);
          set({ user: null, token: null, loading: false });
        }
      },

      setUser: (user, token) => {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        set({
          user,
          token,
          enrolledCourses: user?.enrolledCourses || [],
          completedLessons: user?.completedLessons || {},
          accessTier: user?.accessTier || 'free',
          loading: false
        });
      },

      logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        set({
          user: null,
          token: null,
          enrolledCourses: [],
          completedLessons: {},
          accessTier: 'free',
          loading: false
        });
      },

      // ============ COURSE MANAGEMENT ============
      enrollInCourse: async (courseId) => {
        const token = get().token;
        if (!token) return;

        try {
          const res = await fetch('https://saintsacademy.co.za/api/courses/enroll', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ courseId }),
          });
          if (!res.ok) throw new Error('Failed to enroll');

          set((state) => ({
            enrolledCourses: state.enrolledCourses.includes(courseId)
              ? state.enrolledCourses
              : [...state.enrolledCourses, courseId],
            completedLessons: {
              ...state.completedLessons,
              [courseId]: [],
            },
          }));
        } catch (err) {
          console.error(err);
        }
      },

      markLessonComplete: async (courseId, lessonId) => {
        const token = get().token;
        if (!token) return;

        try {
          const res = await fetch('https://saintsacademy.co.za/api/courses/complete', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ courseId, lessonId }),
          });
          if (!res.ok) throw new Error('Failed to mark lesson complete');

          set((state) => {
            const courseLessons = state.completedLessons[courseId] || [];
            if (courseLessons.includes(lessonId)) return state;

            return {
              completedLessons: {
                ...state.completedLessons,
                [courseId]: [...courseLessons, lessonId],
              },
            };
          });
        } catch (err) {
          console.error(err);
        }
      },

      setCurrentlyPlaying: (episode) => {
        if (!episode) {
          set({ currentlyPlaying: null });
          return;
        }
        
        set({ 
          currentlyPlaying: episode
        });
        
        if (episode) {
          set((state) => ({
            listeningHistory: [
              { 
                podcastId: episode.id, 
                podcastTitle: episode.title,
                podcastImage: episode.image,
                episodeType: episode.episodeType,
                timestamp: new Date().toISOString(),
                lastListened: new Date().toISOString(),
                totalMinutesListened: 0,
                completionPercentage: 0,
                completed: false,
                pointsAwarded: false
              },
              ...state.listeningHistory.filter(h => h.podcastId !== episode.id).slice(0, 9)
            ]
          }));
        }
      },
      
      setAccessTier: (tier) => set({ accessTier: tier }),

      getCourseProgress: (courseId, totalLessons) => {
        const completed = get().completedLessons[courseId] || [];
        return Math.round((completed.length / totalLessons) * 100);
      },

      // ============ LEARNING TIME TRACKING ============
      addLearningTime: (minutes, type, id = null) => {
        set((state) => {
          const today = new Date().toDateString();
          
          // Reset daily time if new day
          let newDailyTime = state.dailyLearningTime;
          let newLastDate = state.lastLearningDate;
          
          if (state.lastLearningDate !== today) {
            newDailyTime = 0;
            newLastDate = today;
          }
          
          const roundedMinutes = Math.round(minutes * 100) / 100;
          const newLearningTime = state.learningTime + roundedMinutes;
          const newDailyLearningTime = newDailyTime + roundedMinutes;
          
          let newCourseLearningTime = { ...state.courseLearningTime };
          let newPodcastLearningTime = { ...state.podcastLearningTime };
          
          if (type === 'course' && id) {
            newCourseLearningTime[id] = (newCourseLearningTime[id] || 0) + roundedMinutes;
          } else if (type === 'podcast' && id) {
            newPodcastLearningTime[id] = (newPodcastLearningTime[id] || 0) + roundedMinutes;
          }
          
          return {
            learningTime: Number(newLearningTime.toFixed(2)),
            dailyLearningTime: Number(newDailyLearningTime.toFixed(2)),
            lastLearningDate: newLastDate,
            courseLearningTime: newCourseLearningTime,
            podcastLearningTime: newPodcastLearningTime
          };
        });
      },

      getTotalLearningTime: () => {
        return Number(get().learningTime.toFixed(2));
      },
      
      getDailyLearningTime: () => {
        const state = get();
        const today = new Date().toDateString();
        if (state.lastLearningDate !== today) {
          return 0;
        }
        return Number(state.dailyLearningTime.toFixed(2));
      },

      getCourseLearningTime: (courseId) => {
        return Number((get().courseLearningTime[courseId] || 0).toFixed(2));
      },

      getPodcastLearningTime: (podcastId) => {
        return Number((get().podcastLearningTime[podcastId] || 0).toFixed(2));
      },

      // ============ PODCAST STATS ============
      getCompletedPodcastsCount: () => {
        const history = get().listeningHistory || [];
        return history.filter(item => item.completed && item.completionPercentage >= 85).length;
      },

      getTotalPodcastsListened: () => {
        const history = get().listeningHistory || [];
        return history.filter(h => h.completionPercentage >= 85).length;
      },

      getPodcastListeningStats: () => {
        const history = get().listeningHistory || [];
        
        const totalMinutesListened = history.reduce((sum, item) => sum + (item.totalMinutesListened || 0), 0);
        const completedPodcasts = history.filter(item => item.completionPercentage >= 85).length;
        const partiallyListened = history.filter(item => 
          item.completionPercentage >= 60 && item.completionPercentage < 85
        ).length;
        const startedPodcasts = history.filter(item => 
          item.totalMinutesListened > 0 && item.completionPercentage < 60
        ).length;
        
        const averageCompletion = history.length > 0 
          ? Number((history.reduce((sum, item) => sum + (item.completionPercentage || 0), 0) / history.length).toFixed(1))
          : 0;
        
        return {
          totalMinutesListened: Number(totalMinutesListened.toFixed(2)),
          completedPodcasts,
          partiallyListened,
          startedPodcasts,
          totalPodcastsListened: history.length,
          averageCompletion
        };
      },

      // ============ PODCAST LISTENING WITH ANTI-CHEATING ============
      calculatePodcastPoints: (podcast, listenDuration, isComplete = false) => {
        return calculatePodcastPoints(podcast, listenDuration, isComplete);
      },

      addPodcastListening: (podcast, listenDuration, isComplete = false) => {
        const state = get();
        
        let podcastDuration = 30;
        if (podcast.duration) {
          const durationMatch = podcast.duration.match(/(\d+)/);
          if (durationMatch) {
            podcastDuration = parseInt(durationMatch[1]);
          }
        }
        
        const completionPercentage = (listenDuration / podcastDuration) * 100;
        
        // Anti-cheating: 85% threshold, reasonable duration check
        const reasonableDuration = listenDuration <= podcastDuration * 1.05;
        const shouldCountAsCompleted = (isComplete || completionPercentage >= 85) && reasonableDuration;
        const shouldCountAsListened = completionPercentage >= 60 && reasonableDuration;
        
        set((state) => {
          const existingIndex = state.listeningHistory.findIndex(h => h.podcastId === podcast.id);
          
          if (existingIndex >= 0) {
            const updatedHistory = [...state.listeningHistory];
            const currentMinutes = updatedHistory[existingIndex].totalMinutesListened || 0;
            const newMinutes = Math.max(currentMinutes, listenDuration);
            
            updatedHistory[existingIndex] = {
              ...updatedHistory[existingIndex],
              podcastTitle: podcast.title,
              podcastImage: podcast.image,
              episodeType: podcast.episodeType,
              totalMinutesListened: Number(newMinutes.toFixed(2)),
              completionPercentage: Math.min(100, Number(((newMinutes / podcastDuration) * 100).toFixed(1))),
              lastListened: new Date().toISOString(),
              completed: shouldCountAsCompleted || updatedHistory[existingIndex].completed,
              pointsAwarded: (shouldCountAsCompleted && !updatedHistory[existingIndex].pointsAwarded) 
                ? true 
                : updatedHistory[existingIndex].pointsAwarded
            };
            
            return {
              listeningHistory: updatedHistory
            };
          } else {
            return {
              listeningHistory: [
                { 
                  podcastId: podcast.id,
                  podcastTitle: podcast.title,
                  podcastImage: podcast.image,
                  episodeType: podcast.episodeType,
                  totalMinutesListened: Number(listenDuration.toFixed(2)),
                  completionPercentage: Number(((listenDuration / podcastDuration) * 100).toFixed(1)),
                  lastListened: new Date().toISOString(),
                  completed: shouldCountAsCompleted,
                  pointsAwarded: shouldCountAsCompleted,
                  timestamp: new Date().toISOString()
                },
                ...state.listeningHistory
              ].slice(0, 50)
            };
          }
        });
        
        state.addLearningTime(listenDuration, 'podcast', podcast.id);
        
        const points = calculatePodcastPoints(podcast, listenDuration, shouldCountAsCompleted);
        if (points > 0 && shouldCountAsListened) {
          const activityType = shouldCountAsCompleted ? 'podcast_complete' : 'podcast_listen';
          state.addPoints(points, activityType, { 
            podcastType: podcast.episodeType, 
            minutes: listenDuration,
            completion: Math.round(completionPercentage)
          });
        }
        
        if (shouldCountAsCompleted) {
          state.checkBadgeUnlocks('podcast_complete');
        }
        
        return shouldCountAsCompleted;
      },

      getContinueListeningPodcasts: () => {
        const state = get();
        return (state.listeningHistory || [])
          .filter(h => {
            return h.totalMinutesListened > 0 && 
                   !h.completed && 
                   h.completionPercentage >= 20 && 
                   h.completionPercentage < 85 &&
                   h.podcastTitle;
          })
          .map(h => ({
            id: h.podcastId,
            title: h.podcastTitle,
            image: h.podcastImage,
            episodeType: h.episodeType,
            progress: Math.min(84, Math.round(h.completionPercentage || 0)),
            lastListened: h.lastListened,
            currentTime: (h.totalMinutesListened || 0) * 60
          }))
          .sort((a, b) => new Date(b.lastListened) - new Date(a.lastListened))
          .slice(0, 5);
      },

      getPodcastProgress: (podcastId) => {
        const state = get();
        const history = state.listeningHistory.find(h => h.podcastId === podcastId);
        if (!history) return { progress: 0, completed: false, percentage: 0 };
        
        return {
          progress: Number(history.totalMinutesListened?.toFixed(2) || 0),
          completed: history.completed || false,
          percentage: history.completionPercentage || 0,
          lastListened: history.lastListened
        };
      },

      // ============ ACCESS CONTROL ============
      canAccessCourse: (courseTier) => {
        const { accessTier } = get();
        
        if (courseTier === 'free') return true;
        if (courseTier === 'premium' && (accessTier === 'plus' || accessTier === 'pro' || accessTier === 'business' || accessTier === 'premium_access')) return true;
        if (courseTier === 'scholarship' && (accessTier === 'pro' || accessTier === 'business' || accessTier === 'premium_access')) return true;
        
        return false;
      },

      // ============ TOAST NOTIFICATIONS ============
      addToast: (toast) => {
        const id = Date.now().toString();
        
        set(state => {
          const existingToasts = state.toasts || [];
          const isDuplicate = existingToasts.some(t => 
            t.message === toast.message && t.points === toast.points
          );
          
          if (isDuplicate) {
            return state;
          }
          
          return {
            toasts: [...existingToasts, { ...toast, id }]
          };
        });
        
        setTimeout(() => {
          get().removeToast(id);
        }, 2000);
      },

      removeToast: (id) => {
        set(state => ({
          toasts: (state.toasts || []).filter(toast => toast.id !== id)
        }));
      },

      // ============ PROFILE PICTURE ============
      setProfilePicture: (pictureUrl) => {
        set({ profilePicture: pictureUrl });
        localStorage.setItem('profilePicture', pictureUrl);
      },

      removeProfilePicture: () => {
        set({ profilePicture: null });
        localStorage.removeItem('profilePicture');
      },

      // ============ POINTS & GAMIFICATION ============
      addPoints: (points, activityType, metadata = {}) => {
        const state = get();
        const today = new Date().toDateString();
        const yesterday = new Date(Date.now() - 86400000).toDateString();
        
        let newStreak = state.dailyStreak;
        if (state.lastActivityDate === yesterday) {
          newStreak = state.dailyStreak + 1;
        } else if (state.lastActivityDate !== today) {
          newStreak = 1;
        }
        
        const newPoints = state.userPoints + points;
        const newLevel = Math.floor(newPoints / 1200) + 1; // More challenging: 1200 per level
        
        const ranks = {
          1: 'Novice', 
          5: 'Explorer', 
          10: 'Scholar', 
          15: 'Adept', 
          20: 'Expert', 
          25: 'Master',
          30: 'Grand Master', 
          40: 'Legend'
        };
        
        let newRank = 'Novice';
        for (const [level, rank] of Object.entries(ranks).reverse()) {
          if (newLevel >= parseInt(level)) {
            newRank = rank;
            break;
          }
        }
        
        set({
          userPoints: Number(newPoints.toFixed(2)),
          userLevel: newLevel,
          userRank: newRank,
          dailyStreak: newStreak,
          lastActivityDate: today
        });
        
        state.checkBadgeUnlocks(activityType, points);
        
        const message = getPointsMessage(activityType, metadata);
        state.addToast({
          message: message,
          points: points,
          type: 'points'
        });
        
        return points;
      },
      
      checkBadgeUnlocks: (activityType, points) => {
        const state = get();
        const newBadges = [];
        
        // Streak badges
        if (state.dailyStreak >= 7 && !state.badges.includes('week_warrior')) {
          newBadges.push('week_warrior');
        }
        if (state.dailyStreak >= 30 && !state.badges.includes('month_master')) {
          newBadges.push('month_master');
        }
        
        // Points milestones - higher thresholds
        if (state.userPoints >= 1200 && !state.badges.includes('point_milestone_1k')) {
          newBadges.push('point_milestone_1k');
        }
        if (state.userPoints >= 6000 && !state.badges.includes('point_milestone_5k')) {
          newBadges.push('point_milestone_5k');
        }
        if (state.userPoints >= 12000 && !state.badges.includes('point_milestone_10k')) {
          newBadges.push('point_milestone_10k');
        }
        
        // Podcast badges - only for completed podcasts (85%+)
        if (activityType === 'podcast_complete') {
          const completedPodcasts = state.listeningHistory.filter(h => h.completionPercentage >= 85).length;
          
          if (completedPodcasts >= 1 && !state.badges.includes('podcast_listener')) {
            newBadges.push('podcast_listener');
          }
          if (completedPodcasts >= 5 && !state.badges.includes('podcast_enthusiast')) {
            newBadges.push('podcast_enthusiast');
          }
          if (completedPodcasts >= 15 && !state.badges.includes('podcast_pro')) {
            newBadges.push('podcast_pro');
          }
          if (completedPodcasts >= 30 && !state.badges.includes('podcast_master')) {
            newBadges.push('podcast_master');
          }
        }
        
        // Deep Diver badge
        const deepDiveCount = (state.listeningHistory || [])
          .filter(h => h.episodeType === 'Deep Dive' && h.completionPercentage >= 85).length;
        if (deepDiveCount >= 3 && !state.badges.includes('deep_diver')) {
          newBadges.push('deep_diver');
        }
        
        // Course badges
        if (activityType === 'course_complete' && !state.badges.includes('course_completer')) {
          newBadges.push('course_completer');
        }
        
        // First lesson
        if (activityType === 'lesson_complete' && !state.badges.includes('first_lesson')) {
          const totalLessons = Object.values(state.completedLessons).reduce((sum, lessons) => sum + lessons.length, 0);
          if (totalLessons >= 1) {
            newBadges.push('first_lesson');
          }
        }
        
        // Attentive listener badge
        const listeningStats = state.getPodcastListeningStats();
        if (listeningStats.totalPodcastsListened >= 3 && 
            listeningStats.averageCompletion >= 85 && 
            !state.badges.includes('attentive_listener')) {
          newBadges.push('attentive_listener');
        }
        
        if (newBadges.length > 0) {
          set({ badges: [...state.badges, ...newBadges] });
          
          newBadges.forEach(badgeId => {
            const badgePoints = {
              'week_warrior': 150,
              'month_master': 500,
              'point_milestone_1k': 100,
              'point_milestone_5k': 250,
              'point_milestone_10k': 500,
              'podcast_listener': 75,
              'podcast_enthusiast': 150,
              'podcast_pro': 300,
              'podcast_master': 500,
              'deep_diver': 250,
              'course_completer': 50,
              'first_lesson': 10,
              'attentive_listener': 200
            };
            
            const points = badgePoints[badgeId] || 25;
            state.addPoints(points, 'badge_unlock', { badgeId });
          });
        }
      },
      
      unlockBadge: (badgeId) => {
        set(state => ({
          badges: [...state.badges, badgeId]
        }));
      },
      
      completeLessonWithPoints: async (courseId, lessonId, courseData) => {
        const state = get();
        
        await state.markLessonComplete(courseId, lessonId);
        
        if (courseData) {
          const points = calculateCoursePoints(courseData, 'lesson_complete', courseData.skillLevel);
          state.addPoints(points, 'lesson_complete');
          
          const module = courseData.modules.find(m => m.lessons.some(l => l.id === lessonId));
          if (module) {
            const moduleLessons = module.lessons;
            const completedLessonsInModule = state.completedLessons[courseId]?.filter(id => 
              moduleLessons.some(lesson => lesson.id === id)
            ) || [];
            
            if (completedLessonsInModule.length === moduleLessons.length) {
              const modulePoints = calculateCoursePoints(courseData, 'module_complete', courseData.skillLevel);
              state.addPoints(modulePoints, 'module_complete');
            }
          }
          
          const totalLessons = courseData.modules.reduce((sum, m) => sum + m.lessons.length, 0);
          if (state.completedLessons[courseId]?.length === totalLessons) {
            const coursePoints = calculateCoursePoints(courseData, 'course_complete', courseData.skillLevel);
            state.addPoints(coursePoints, 'course_complete');
          }
          
          return points;
        }
        return 0;
      },

      updateDailyStreak: () => {
        const state = get();
        const today = new Date().toDateString();
        const lastDate = state.lastActivityDate ? new Date(state.lastActivityDate).toDateString() : null;
        
        if (lastDate === today) return;
        
        if (!lastDate || isConsecutiveDay(lastDate, today)) {
          const newStreak = lastDate ? state.dailyStreak + 1 : 1;
          set({ 
            dailyStreak: newStreak,
            lastActivityDate: new Date().toISOString()
          });
          
          const streakBonus = pointsSystem.daily.streakBonus(newStreak);
          if (streakBonus) {
            state.addPoints(streakBonus, 'streak_bonus', { streak: newStreak });
          }
        } else {
          set({ 
            dailyStreak: 1,
            lastActivityDate: new Date().toISOString()
          });
        }
      },

      // ============ RESET ============
      resetStore: () => set({
        user: null,
        token: null,
        enrolledCourses: [],
        completedLessons: {},
        currentlyPlaying: null,
        accessTier: 'free',
        listeningHistory: [],
        userPoints: 0,
        userLevel: 1,
        userRank: 'Novice',
        badges: [],
        dailyStreak: 0,
        lastActivityDate: null,
        learningTime: 0,
        dailyLearningTime: 0,
        lastLearningDate: new Date().toDateString(),
        courseLearningTime: {},
        podcastLearningTime: {},
        toasts: [],
        profilePicture: null,
        loading: false
      })
    }),
    {
      name: 'saints-academy-storage',
      partialize: (state) => ({ 
        user: state.user,
        token: state.token,
        accessTier: state.accessTier,
        enrolledCourses: state.enrolledCourses,
        completedLessons: state.completedLessons,
        listeningHistory: state.listeningHistory,
        userPoints: state.userPoints,
        userLevel: state.userLevel,
        userRank: state.userRank,
        badges: state.badges,
        dailyStreak: state.dailyStreak,
        lastActivityDate: state.lastActivityDate,
        learningTime: state.learningTime,
        dailyLearningTime: state.dailyLearningTime,
        lastLearningDate: state.lastLearningDate,
        courseLearningTime: state.courseLearningTime,
        podcastLearningTime: state.podcastLearningTime,
        profilePicture: state.profilePicture
      })
    }
  )
);

// Initialize store
const store = useStore;
store.getState().loadUser();

export default useStore;